import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import creditCardTypes, {
  CREDIT_CARDS_TYPES_ICONS,
} from '../../util/enum/api/creditCardTypes';
import { formatDate } from '../../util/helpers/dateHelpers';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import { Dot } from '../StorageFee/AutoPayStatus';
import { ReactComponent as Trash } from '../../assets/images/svg/trash.svg';

export const DotAndTextWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${pxToRem(10)};
`;

export const AutoPayText = styled.div`
  font-size: ${pxToRem(12)};
  font-weight: 500;
  color: ${themeColors.colorSuccess};
`;

export const RemoveActionLineWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

export const CreditCardButton = styled.div`
  position: absolute;
  bottom: ${pxToRem(5)};
  right: ${pxToRem(10)};
  display: flex;

  > :first-child {
    font-size: ${pxToRemMd(12)};
    margin-right: ${pxToRem(8)};
  }
`;

export const AddressCardDetails = styled.div`
  display: flex;
  align-items: center;
  font-size: ${pxToRem(14)};
  line-height: 1.75;
  text-align: left;
  color: ${themeColors.colorTextPrimary};
  font-weight: 500;

  span {
    font-size: ${pxToRem(12)};
    font-weight: 400;
    color: ${themeColors.colorTextSecondary};
    display: block;
  }
`;

export const AddressCardDefault = styled.p`
  position: absolute;
  top: 0;
  right: 0;
  background-color: ${themeColors.colorTextSecondary};
  color: ${themeColors.colorWhite};
  display: inline-flex;
  padding: 2px 6px;
  justify-content: center;
  align-items: center;
  font-size: ${pxToRem(14)};
  font-weight: 500;
  border-radius: 0 3px 0 2px;
`;

export const AddressCardName = styled.p`
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-weight: 500;
  }
`;

export const AddressCardAddress = styled.p`
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-weight: 500;
  }
`;

export const CreditCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid transparent;
  border-radius: ${variables.borderRadius.borderRadius};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  background-color: ${themeColors.colorBackgroundSecondary};
  position: relative;
  padding: 10px;
  margin-bottom: 10px;
  ${(props) =>
    props.isAddressCardDefault && `border-color: ${themeColors.colorFocus}`};
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  min-width: 0;
`;

const CreditCardContent = styled.div`
  display: flex;
  align-items: center;
  font-size: ${pxToRem(14)};
  line-height: 1.75;
  text-align: left;
  color: ${themeColors.colorTextPrimary};
  font-weight: 500;
  width: 100%;

  span {
    font-size: ${pxToRem(12)};
    font-weight: 400;
    color: ${themeColors.colorTextSecondary};
    display: block;
  }
`;

const CreditCardIcon = styled.div`
  width: ${pxToRem(32)};
  height: ${pxToRem(32)};
  background-color: ${themeColors.colorGrey};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: ${pxToRem(10)};
  border-radius: ${variables.borderRadius.borderRadius};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    width: ${pxToRemMd(28)};
    height: ${pxToRemMd(28)};
  }
`;

const SelectItemContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const SelectItemSubtitleWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CreditCardTitle = styled.p`
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorTextPrimary};
  font-weight: 500;
`;

const MaskedContainer = styled.div`
  font-size: ${pxToRem(12)};
  color: ${themeColors.colorTextSecondary};
  display: flex;
  align-self: flex-start;
  span {
    font-size: ${pxToRem(12)};
  }
  span:first-child {
    margin-right: ${pxToRem(12)};
  }
`;

const CreditCard = ({ creditCard, handleRemoveCreditCard }) => {
  const { t } = useTranslation();

  return (
    <CreditCardWrapper key={creditCard.CreditCardUid}>
      <CreditCardContent>
        <CreditCardIcon>
          {CREDIT_CARDS_TYPES_ICONS[creditCard.CreditCardType]}
        </CreditCardIcon>
        <SelectItemContent>
          <CreditCardTitle> {creditCard.NameOnCard}</CreditCardTitle>
          <SelectItemSubtitleWrap>
            <MaskedContainer>
              <span>
                {t(
                  `creditCards.creditCardTypes.${
                    creditCardTypes[creditCard.CreditCardType]
                  }`,
                )}
              </span>
              <span>***</span>
              <span>{creditCard?.Last4Digits}</span>
            </MaskedContainer>
          </SelectItemSubtitleWrap>
          <MaskedContainer>
            <span>
              {t('creditCards.expires', {
                expirationMonth: formatDate(creditCard.ExpirationDate, 'MM'),
                expirationYear: formatDate(creditCard.ExpirationDate, 'yy'),
              })}
            </span>
          </MaskedContainer>
        </SelectItemContent>
      </CreditCardContent>
      {creditCard.IsDefaultForStorage && (
        <DotAndTextWrapper>
          <Dot isAutoPayOn />
          <AutoPayText>{t('creditCards.thisCardIsUsedForAutoPay')}</AutoPayText>
        </DotAndTextWrapper>
      )}
      <CreditCardButton>
        <DeleteButton
          onClick={() => handleRemoveCreditCard(creditCard.CreditCardUid)}
        >
          <Trash />
        </DeleteButton>
      </CreditCardButton>
    </CreditCardWrapper>
  );
};

export default CreditCard;

CreditCard.propTypes = {
  creditCard: PropTypes.shape({
    CreditCardUid: PropTypes.string,
    NameOnCard: PropTypes.string,
    CreditCardType: PropTypes.number,
    Last4Digits: PropTypes.string,
    ExpirationDate: PropTypes.string,
    IsDefaultForStorage: PropTypes.bool,
  }),
  handleRemoveCreditCard: PropTypes.func,
};
